import React from "react";
import { MapPin } from "lucide-react";
import useReveal from "../hooks/useReveal";
import ContactSection from "../components/ContactSection";
import { IMAGES, META } from "../mock";
import useDocMeta from "../hooks/useDocMeta";

export default function Contact() {
  useReveal();
  useDocMeta(META.contact);
  return (
    <div>
      <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-16 bg-gradient-to-b from-brand-50 to-sand-50 overflow-hidden">
        <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-brand-200/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">Get in touch</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-brand-900 text-balance">Contact AwareHub TMS and Esketamine Clinics</h1>
          <p className="mt-5 text-lg text-brand-900/70 max-w-2xl mx-auto" data-testid="contact-intro-text">
            Locations in Bondi Junction, Sydney, NSW and Bundaberg, QLD. New locations coming soon in Gladstone &amp; Hervey Bay!
          </p>
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 mt-10">
          <div className="grid grid-cols-3 gap-3 sm:gap-5" data-testid="contact-photo-strip">
            {[
              { src: IMAGES.greenWall, pos: "50% 50%" },
              { src: IMAGES.quietZone, pos: "50% 75%" },
              { src: IMAGES.communityZone, pos: "50% 50%" },
            ].map((p, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3] shadow-[0_24px_60px_-48px_rgba(31,56,53,0.5)]">
                <img src={p.src} alt="AwareHub clinic" className="h-full w-full object-cover" style={{ objectPosition: p.pos }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactSection />

      {/* Location & directions */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-6">
          {/* Sydney */}
          <div className="reveal rounded-[2rem] overflow-hidden border border-brand-200 shadow-[0_30px_70px_-45px_rgba(34,63,60,0.5)] bg-white flex flex-col">
            <div className="p-7 sm:p-8 bg-gradient-to-br from-brand-100 to-brand-50">
              <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">AwareHub Sydney</span>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Suite+703%2F1+Newland+Street+Bondi+Junction+NSW+2022"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-start gap-2 text-brand-800 hover:text-brand-600 font-medium transition-colors"
                data-testid="address-sydney"
              >
                <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-brand-500" />
                Suite 703/1 Newland Street, Bondi Junction, 2022, NSW, Australia
              </a>
            </div>
            <div className="min-h-[300px] flex-1">
              <iframe
                title="AwareHub Sydney TMS location"
                src="https://www.google.com/maps?q=Suite%20703%2F1%20Newland%20Street%2C%20Bondi%20Junction%20NSW%202022&output=embed"
                className="w-full h-full min-h-[300px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Bundaberg */}
          <div className="reveal rounded-[2rem] overflow-hidden border border-brand-200 shadow-[0_30px_70px_-45px_rgba(34,63,60,0.5)] bg-white flex flex-col">
            <div className="p-7 sm:p-8 bg-gradient-to-br from-brand-100 to-brand-50">
              <span className="text-brand-500 font-semibold tracking-[0.2em] text-xs uppercase">AwareHub Bundaberg</span>
              <a
                href="https://www.google.com/maps/search/?api=1&query=The+Friendlies+Medical+Suites+70-72+Crofton+Street+Bundaberg+QLD+4670"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-start gap-2 text-brand-800 hover:text-brand-600 font-medium transition-colors"
                data-testid="address-bundaberg"
              >
                <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-brand-500" />
                The Friendlies Medical Suites, 70-72 Crofton Street, Bundaberg, 4670, QLD, Australia
              </a>
            </div>
            <div className="min-h-[300px] flex-1">
              <iframe
                title="AwareHub Bundaberg location"
                src="https://www.google.com/maps?q=The%20Friendlies%20Medical%20Suites%2C%2070-72%20Crofton%20Street%2C%20Bundaberg%20QLD%204670&output=embed"
                className="w-full h-full min-h-[300px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="reveal rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[16/10] relative">
              <img src={IMAGES.sydneyReception} alt="AwareHub Sydney reception" className="h-full w-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-brand-950/80 to-transparent p-6">
                <p className="text-white font-display text-xl">AwareHub Sydney</p>
                <p className="text-brand-100 text-sm">Bondi Junction, NSW</p>
              </div>
            </div>
            <div className="reveal rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-45px_rgba(31,56,53,0.5)] aspect-[16/10] relative">
              <img src={IMAGES.friendliesBuilding} alt="The Friendlies Medical Suites, Bundaberg" className="h-full w-full object-cover" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-brand-950/80 to-transparent p-6">
                <p className="text-white font-display text-xl">AwareHub Bundaberg</p>
                <p className="text-brand-100 text-sm">The Friendlies Medical Suites, QLD</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
